import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import {
  Card,
  Form,
  TimePicker,
  Switch,
  Button,
  message,
  Spin,
  Alert,
  Tag,
  Tooltip,
  Input,
  Modal,
  Select,
  Statistic,
  Row,
  Col,
  Upload,
  Image,
} from "antd";
import {
  SaveOutlined,
  SendOutlined,
  SyncOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  ThunderboltOutlined,
  ExperimentOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customClientAxios from "../customAxios";
import {
  PlatformSettings as PlatformSettingsEndpoint,
  AdminNurtureTemplate,
  AdminNurtureTemplateBody,
  AdminNurtureTemplateSubmit,
  AdminNurtureTemplateSyncStatus,
  AdminNurtureTemplateHeaderMedia,
  PlanExpiryRunSweep,
  PlanExpiryTestSend,
  PlanExpiryStats,
  PlanExpiryPostBlast,
} from "../utils/ApiEndpoints";

const PageWrapper = styled.div`
  padding: 8px 4px 32px;
  animation: fadeIn 0.3s ease;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;
const PageHeader = styled.div`
  margin-bottom: 24px;
`;
const PageTitle = styled.h1`
  font-size: 28px; font-weight: 700; color: #0f172a;
  letter-spacing: -0.5px; margin: 0;
`;
const PageSubtitle = styled.p`
  font-size: 14px; color: #64748b; margin: 6px 0 0; max-width: 720px;
`;
const SectionTitle = styled.h2`
  font-size: 16px; font-weight: 600; color: #0f172a; margin: 0 0 4px;
`;
const SectionHint = styled.p`
  font-size: 13px; color: #64748b; margin: 0 0 16px;
`;
const FieldLabel = styled.div`
  font-size: 13px; font-weight: 500; color: #475569;
  display: flex; align-items: center; gap: 6px;
`;
const FieldHelp = styled.div`
  font-size: 12px; color: #94a3b8; margin-top: 4px;
`;
const Meta = styled.div`
  font-size: 12px; color: #94a3b8; margin-top: 12px;
`;

const STATUS_META = {
  draft: { color: "default", label: "Draft", Icon: EditOutlined },
  pending: { color: "processing", label: "Awaiting Meta approval", Icon: ClockCircleOutlined },
  approved: { color: "success", label: "Approved", Icon: CheckCircleOutlined },
  rejected: { color: "error", label: "Rejected", Icon: CloseCircleOutlined },
};

function StatusTag({ status, rejectionReason }) {
  const meta = STATUS_META[status] || { color: "default", label: status || "unknown" };
  const Icon = meta.Icon || InfoCircleOutlined;
  return (
    <Tooltip title={rejectionReason || meta.label}>
      <Tag color={meta.color} icon={<Icon />}>
        {meta.label}
      </Tag>
    </Tooltip>
  );
}

// ─── Settings card ───────────────────────────────────────────────────────────
function SettingsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [reminderTime, setReminderTime] = useState(dayjs("21:00", "HH:mm"));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(PlatformSettingsEndpoint);
      const data = res.data?.data;
      setSettings(data);
      setReminderEnabled(!!data?.planExpiryReminders?.enabled);
      setReminderTime(
        dayjs(data?.planExpiryReminders?.triggerTimeIST || "21:00", "HH:mm")
      );
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        planExpiryReminders: {
          enabled: reminderEnabled,
          triggerTimeIST: reminderTime.format("HH:mm"),
        },
      };
      const res = await customClientAxios().put(PlatformSettingsEndpoint, payload);
      setSettings(res.data?.data);
      message.success("Settings saved");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card style={{ borderRadius: 16, marginBottom: 20 }}>
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ borderRadius: 16, marginBottom: 20 }}>
        <Alert type="error" showIcon message={error} />
      </Card>
    );
  }

  return (
    <Card style={{ borderRadius: 16, marginBottom: 20 }} bodyStyle={{ padding: 24 }}>
      <SectionTitle>Reminder schedule</SectionTitle>
      <SectionHint>
        The daily sweeper picks up subscriptions about to expire and sends a
        WhatsApp utility message to the brand-page owner. Adjust when it fires.
      </SectionHint>

      <Form layout="vertical" style={{ maxWidth: 480 }}>
        <Form.Item
          label={<FieldLabel>Enable reminders</FieldLabel>}
          style={{ marginBottom: 18 }}
        >
          <Switch checked={reminderEnabled} onChange={setReminderEnabled} />
          <FieldHelp>
            When off, the sweeper skips all sends. Templates and data are untouched.
          </FieldHelp>
        </Form.Item>

        <Form.Item
          label={
            <FieldLabel>
              Daily trigger time (IST)
              <Tooltip title="Cron fires once a day at this time in India Standard Time. 24-hour format.">
                <InfoCircleOutlined style={{ color: "#94a3b8" }} />
              </Tooltip>
            </FieldLabel>
          }
          style={{ marginBottom: 18 }}
        >
          <TimePicker
            value={reminderTime}
            onChange={(t) => t && setReminderTime(t)}
            format="HH:mm"
            minuteStep={5}
            showNow={false}
            allowClear={false}
            style={{ width: 160 }}
          />
          <FieldHelp>
            Default 21:00 (9pm IST). All that day's reminders fire at this time.
          </FieldHelp>
        </Form.Item>

        <Form.Item label={<FieldLabel>Reminder windows</FieldLabel>} style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", gap: 8 }}>
            {(settings?.planExpiryReminders?.windowDays || [5, 1]).map((d) => (
              <Tag key={d} color="blue">
                {d} day{d > 1 ? "s" : ""} before expiry
              </Tag>
            ))}
          </div>
          <FieldHelp>
            Read-only for now. Each window maps to a different WhatsApp template
            (edit content below).
          </FieldHelp>
        </Form.Item>

        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          Save schedule
        </Button>
      </Form>

      {settings?.updatedAt && (
        <Meta>
          Last updated {dayjs(settings.updatedAt).format("D MMM YYYY, HH:mm")} IST
          {settings.updatedBy ? ` by ${settings.updatedBy}` : ""}
        </Meta>
      )}
    </Card>
  );
}

// ─── Template editor card ────────────────────────────────────────────────────
function TemplateCard({ sequenceKey, label, variableHint, allowHeaderImage = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [buttonText, setButtonText] = useState("");

  const stepDay = 1;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(
        AdminNurtureTemplate(stepDay, sequenceKey)
      );
      const d = res.data?.data;
      setData(d);
      // Extract body + button text from msg91 components
      const components = d?.template?.components || [];
      const bodyComp = components.find((c) => c.type === "BODY");
      const btnComp = components.find((c) => c.type === "BUTTONS");
      setBody(bodyComp?.text || "");
      setButtonText(btnComp?.buttons?.[0]?.text || "");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load template");
    } finally {
      setLoading(false);
    }
  }, [sequenceKey]);

  useEffect(() => {
    load();
  }, [load]);

  const status = data?.template?.status || "draft";
  const isEditable = status === "draft" || status === "rejected";

  const handleSaveBody = async () => {
    setSaving(true);
    try {
      await customClientAxios().patch(
        AdminNurtureTemplateBody(stepDay, sequenceKey),
        { body, buttonText, category: "UTILITY" }
      );
      message.success("Template body saved");
      await load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await customClientAxios().post(AdminNurtureTemplateSubmit(stepDay, sequenceKey));
      message.success("Submitted to Meta — approval typically takes 24-48 hours");
      await load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await customClientAxios().post(AdminNurtureTemplateSyncStatus(stepDay, sequenceKey));
      message.success("Status refreshed from Meta");
      await load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleUploadImage = async (file) => {
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await customClientAxios().post(
        AdminNurtureTemplateHeaderMedia(stepDay, sequenceKey),
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      message.success("Header image uploaded — submit to Meta to apply it");
      await load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Image upload failed");
    } finally {
      setUploadingImage(false);
    }
    return false; // prevent antd's default upload
  };

  if (loading) {
    return (
      <Card style={{ borderRadius: 16, marginBottom: 20 }}>
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ borderRadius: 16, marginBottom: 20 }}>
        <Alert type="error" showIcon message={error} />
      </Card>
    );
  }

  const tpl = data?.template;

  return (
    <Card
      style={{ borderRadius: 16, marginBottom: 20 }}
      bodyStyle={{ padding: 24 }}
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 400, marginTop: 2 }}>
              <code style={{ fontSize: 11, background: "#f1f5f9", padding: "2px 6px", borderRadius: 4 }}>
                {tpl?.name}
              </code>
              <span style={{ marginLeft: 10 }}>
                <StatusTag status={status} rejectionReason={tpl?.rejectionReason} />
              </span>
            </div>
          </div>
          <Button
            size="small"
            icon={<SyncOutlined />}
            onClick={handleSync}
            loading={syncing}
          >
            Refresh status
          </Button>
        </div>
      }
    >
      <Form layout="vertical">
        <Form.Item
          label={<FieldLabel>Message body</FieldLabel>}
          style={{ marginBottom: 14 }}
        >
          <Input.TextArea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            disabled={!isEditable}
          />
          <FieldHelp>{variableHint}</FieldHelp>
        </Form.Item>

        <Form.Item
          label={<FieldLabel>Button text</FieldLabel>}
          style={{ marginBottom: 14 }}
        >
          <Input
            value={buttonText}
            onChange={(e) => setButtonText(e.target.value)}
            style={{ maxWidth: 320 }}
            disabled={!isEditable}
          />
          <FieldHelp>
            URL: <code>https://growezy.club/r/{`{{1}}`}</code> (click-tracked, redirects to{" "}
            <code>/subscription</code> on the creator dashboard)
          </FieldHelp>
        </Form.Item>

        {allowHeaderImage && (
          <Form.Item label={<FieldLabel>Header image</FieldLabel>} style={{ marginBottom: 14 }}>
            {tpl?.headerMedia?.url && (
              <div style={{ marginBottom: 10 }}>
                <Image
                  src={tpl.headerMedia.url}
                  alt="Header"
                  style={{ maxWidth: 220, borderRadius: 8, border: "1px solid #e2e8f0" }}
                />
              </div>
            )}
            <Upload
              accept="image/jpeg,image/png"
              showUploadList={false}
              beforeUpload={handleUploadImage}
              disabled={!isEditable || uploadingImage}
            >
              <Button icon={<PictureOutlined />} loading={uploadingImage} disabled={!isEditable}>
                {tpl?.headerMedia?.url ? "Replace header image" : "Upload header image"}
              </Button>
            </Upload>
            <FieldHelp>
              JPEG or PNG, ≤ 5MB. This appears as the image at the top of the
              WhatsApp message. Upload before submitting to Meta.
            </FieldHelp>
          </Form.Item>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveBody}
            loading={saving}
            disabled={!isEditable}
          >
            Save body
          </Button>
          <Button
            icon={<SendOutlined />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!isEditable}
          >
            Submit to Meta for approval
          </Button>
        </div>

        {!isEditable && (
          <Alert
            style={{ marginTop: 14 }}
            type="info"
            showIcon
            message={
              status === "pending"
                ? "Awaiting Meta approval. You can re-submit only after approval / rejection."
                : status === "approved"
                ? "Template is approved. To edit body, first reject the current version via msg91 dashboard (Meta limits live-template edits)."
                : "Template not editable in current state."
            }
          />
        )}
      </Form>
    </Card>
  );
}

// ─── Stats + Ops card ────────────────────────────────────────────────────────
function StatsAndOpsCard() {
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [running, setRunning] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testForm, setTestForm] = useState({
    kind: "5d",
    phone: "",
    name: "",
    plan: "growth",
  });
  const [testing, setTesting] = useState(false);

  const loadStats = useCallback(async () => {
    setStatsError(null);
    try {
      const res = await customClientAxios().get(PlanExpiryStats);
      setStats(res.data?.data);
    } catch (e) {
      setStatsError(e?.response?.data?.message || e.message || "Failed to load stats");
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleRunSweep = async (dryRun) => {
    setRunning(true);
    try {
      const res = await customClientAxios().post(PlanExpiryRunSweep, { dryRun });
      const windows = res.data?.data?.windows || [];
      const total = windows.reduce(
        (acc, w) => ({
          candidates: acc.candidates + w.candidates,
          sent: acc.sent + (w.sent || 0),
          alreadySent: acc.alreadySent + (w.alreadySent || 0),
          skipped: acc.skipped + (w.skipped || 0),
          failed: acc.failed + (w.failed || 0),
        }),
        { candidates: 0, sent: 0, alreadySent: 0, skipped: 0, failed: 0 }
      );
      const verb = dryRun ? "Preview" : "Sweep";
      message.success(
        `${verb}: ${total.candidates} candidates · ${total.sent} sent · ${total.alreadySent} already sent · ${total.skipped} skipped · ${total.failed} failed`
      );
      await loadStats();
    } catch (e) {
      message.error(e?.response?.data?.message || `${dryRun ? "Preview" : "Sweep"} failed`);
    } finally {
      setRunning(false);
    }
  };

  const handleTestSend = async () => {
    if (!testForm.phone) {
      message.error("Phone is required (E.164 format like +919999999999)");
      return;
    }
    setTesting(true);
    try {
      const res = await customClientAxios().post(PlanExpiryTestSend, testForm);
      const data = res.data?.data;
      if (data?.sent) {
        message.success(`Test sent — msg91 request ${data.msg91RequestId}`);
      } else {
        message.warning(`Send returned: ${data?.skipped || data?.failure || "unknown"}`);
      }
      setTestOpen(false);
      await loadStats();
    } catch (e) {
      message.error(e?.response?.data?.message || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  const counts5d = stats?.byKind?.["5d"] || {};
  const counts1d = stats?.byKind?.["1d"] || {};
  const countsPost = stats?.byKind?.["post"] || {};

  return (
    <Card style={{ borderRadius: 16, marginBottom: 20 }} bodyStyle={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <SectionTitle>Operations & last-30-day stats</SectionTitle>
        <div style={{ display: "flex", gap: 8 }}>
          <Tooltip title="Find candidates without sending — preview only">
            <Button onClick={() => handleRunSweep(true)} loading={running}>
              Preview candidates
            </Button>
          </Tooltip>
          <Tooltip title="Run the daily reminder check NOW instead of waiting for the configured time">
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={() => handleRunSweep(false)}
              loading={running}
            >
              Run reminder check now
            </Button>
          </Tooltip>
          <Tooltip title="Send a test message to a phone number">
            <Button icon={<ExperimentOutlined />} onClick={() => setTestOpen(true)}>
              Test send
            </Button>
          </Tooltip>
        </div>
      </div>
      <SectionHint>
        Stats below show non-test sends in the last 30 days. Click rates &gt; 0 require
        recipients to click the WhatsApp button.
      </SectionHint>

      {statsError && <Alert type="error" showIcon message={statsError} style={{ marginBottom: 12 }} />}

      <Row gutter={16}>
        <Col span={8}>
          <Card type="inner" title="5-day reminders" size="small">
            <Row gutter={8}>
              <Col span={8}><Statistic title="Sent" value={counts5d.sent || 0} /></Col>
              <Col span={8}><Statistic title="Delivered" value={counts5d.delivered || 0} /></Col>
              <Col span={8}><Statistic title="Read" value={counts5d.read || 0} /></Col>
            </Row>
            <Row gutter={8} style={{ marginTop: 12 }}>
              <Col span={8}><Statistic title="Clicked" value={counts5d.clicked || 0} valueStyle={{ color: "#10b981" }} /></Col>
              <Col span={8}><Statistic title="Failed" value={counts5d.failed || 0} valueStyle={{ color: counts5d.failed ? "#dc2626" : undefined }} /></Col>
              <Col span={8}><Statistic title="Total" value={counts5d.total || 0} /></Col>
            </Row>
          </Card>
        </Col>
        <Col span={8}>
          <Card type="inner" title="1-day reminders" size="small">
            <Row gutter={8}>
              <Col span={8}><Statistic title="Sent" value={counts1d.sent || 0} /></Col>
              <Col span={8}><Statistic title="Delivered" value={counts1d.delivered || 0} /></Col>
              <Col span={8}><Statistic title="Read" value={counts1d.read || 0} /></Col>
            </Row>
            <Row gutter={8} style={{ marginTop: 12 }}>
              <Col span={8}><Statistic title="Clicked" value={counts1d.clicked || 0} valueStyle={{ color: "#10b981" }} /></Col>
              <Col span={8}><Statistic title="Failed" value={counts1d.failed || 0} valueStyle={{ color: counts1d.failed ? "#dc2626" : undefined }} /></Col>
              <Col span={8}><Statistic title="Total" value={counts1d.total || 0} /></Col>
            </Row>
          </Card>
        </Col>
        <Col span={8}>
          <Card type="inner" title="Post-expiry reminders" size="small">
            <Row gutter={8}>
              <Col span={8}><Statistic title="Sent" value={countsPost.sent || 0} /></Col>
              <Col span={8}><Statistic title="Delivered" value={countsPost.delivered || 0} /></Col>
              <Col span={8}><Statistic title="Read" value={countsPost.read || 0} /></Col>
            </Row>
            <Row gutter={8} style={{ marginTop: 12 }}>
              <Col span={8}><Statistic title="Clicked" value={countsPost.clicked || 0} valueStyle={{ color: "#10b981" }} /></Col>
              <Col span={8}><Statistic title="Failed" value={countsPost.failed || 0} valueStyle={{ color: countsPost.failed ? "#dc2626" : undefined }} /></Col>
              <Col span={8}><Statistic title="Total" value={countsPost.total || 0} /></Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Modal
        open={testOpen}
        title="Test send a renewal reminder"
        onCancel={() => setTestOpen(false)}
        onOk={handleTestSend}
        okText="Send"
        confirmLoading={testing}
      >
        <Form layout="vertical">
          <Form.Item label="Reminder kind">
            <Select
              value={testForm.kind}
              onChange={(v) => setTestForm({ ...testForm, kind: v })}
              options={[
                { value: "5d", label: "5-day reminder" },
                { value: "1d", label: "1-day reminder" },
                { value: "post", label: "Post-expiry reminder" },
              ]}
            />
          </Form.Item>
          <Form.Item label="Phone (E.164)" required>
            <Input
              value={testForm.phone}
              onChange={(e) => setTestForm({ ...testForm, phone: e.target.value })}
              placeholder="+919999999999"
            />
          </Form.Item>
          <Form.Item label="Recipient name (used as {{1}})">
            <Input
              value={testForm.name}
              onChange={(e) => setTestForm({ ...testForm, name: e.target.value })}
              placeholder="Test User"
            />
          </Form.Item>
          <Form.Item label="Plan label (used as {{2}})">
            <Select
              value={testForm.plan}
              onChange={(v) => setTestForm({ ...testForm, plan: v })}
              options={[
                { value: "growth", label: "Growth" },
                { value: "pro", label: "Pro" },
                { value: "advanced", label: "Advanced" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

// ─── On-demand post-expiry blast card ────────────────────────────────────────
function PostExpiryBlastCard() {
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [result, setResult] = useState(null);

  const handlePreview = async () => {
    setPreviewing(true);
    setResult(null);
    try {
      const res = await customClientAxios().post(PlanExpiryPostBlast, { dryRun: true });
      const data = res.data?.data;
      setPreview(data);
      message.success(
        `Preview: ${data?.candidates ?? 0} expired Growth pages (of ${data?.growthPages ?? 0} total)`
      );
    } catch (e) {
      message.error(e?.response?.data?.message || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendLive = async () => {
    setSending(true);
    try {
      const res = await customClientAxios().post(PlanExpiryPostBlast, { dryRun: false });
      const data = res.data?.data;
      setResult(data);
      message.success(
        `Blast done: ${data?.sent ?? 0} sent · ${data?.alreadySent ?? 0} already · ${data?.skipped ?? 0} skipped · ${data?.failed ?? 0} failed`
      );
      setConfirmOpen(false);
      setConfirmText("");
    } catch (e) {
      message.error(e?.response?.data?.message || "Live send failed");
    } finally {
      setSending(false);
    }
  };

  const candidates = preview?.candidates ?? null;

  return (
    <Card
      style={{ borderRadius: 16, marginBottom: 20, border: "1px solid #fecaca" }}
      bodyStyle={{ padding: 24 }}
    >
      <SectionTitle>On-demand: Post-expiry blast to expired Growth pages</SectionTitle>
      <SectionHint>
        One-time bulk send of the post-expiry reminder to every brand page on the{" "}
        <strong>Growth</strong> plan whose latest paid period has already lapsed.
        This is <strong>not</strong> part of the daily sweep. Sends are idempotent —
        re-running won't double-send to the same page/period. The{" "}
        <code>plan_expiry_post</code> template must be approved by Meta first.
      </SectionHint>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Preview first, then send. Always run a preview to confirm the audience count before the live blast."
      />

      <div style={{ display: "flex", gap: 8 }}>
        <Button onClick={handlePreview} loading={previewing}>
          Preview candidates
        </Button>
        <Button
          danger
          type="primary"
          icon={<ThunderboltOutlined />}
          loading={sending}
          disabled={candidates === null || candidates === 0}
          onClick={() => setConfirmOpen(true)}
        >
          Send live to all expired Growth pages
        </Button>
      </div>

      {preview && (
        <div style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Statistic title="Growth pages (total)" value={preview.growthPages ?? 0} />
            </Col>
            <Col span={8}>
              <Statistic
                title="Expired (will be sent)"
                value={preview.candidates ?? 0}
                valueStyle={{ color: "#dc2626" }}
              />
            </Col>
          </Row>
        </div>
      )}

      {result && (
        <div style={{ marginTop: 16 }}>
          <Alert
            type="success"
            showIcon
            message={`Sent ${result.sent} · Already sent ${result.alreadySent} · Skipped ${result.skipped} · Failed ${result.failed}`}
            description={
              result.sampleSkips?.length ? (
                <div style={{ fontSize: 12 }}>
                  Sample skips:{" "}
                  {result.sampleSkips
                    .map((s) => `${s.brandPageId.slice(0, 8)}…: ${s.reason}`)
                    .join(", ")}
                </div>
              ) : null
            }
          />
        </div>
      )}

      <Modal
        open={confirmOpen}
        title="Confirm live blast"
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmText("");
        }}
        onOk={handleSendLive}
        okText={`Send to ${candidates ?? 0} pages`}
        okButtonProps={{ danger: true, disabled: confirmText.trim() !== "SEND" }}
        confirmLoading={sending}
      >
        <p>
          This will send the post-expiry WhatsApp reminder to{" "}
          <strong>{candidates ?? 0}</strong> expired Growth brand pages. Each page's
          owner receives one message. Already-sent pages are skipped automatically.
        </p>
        <p style={{ marginBottom: 6 }}>
          Type <code>SEND</code> to confirm:
        </p>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="SEND"
        />
      </Modal>
    </Card>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function RenewalRemindersPage() {
  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Renewal Reminders</PageTitle>
        <PageSubtitle>
          WhatsApp UTILITY messages sent to brand-page owners whose plan is
          about to expire. Edit schedule + templates here. Sends fire daily at
          the configured trigger time.
        </PageSubtitle>
      </PageHeader>

      <SettingsCard />

      <StatsAndOpsCard />

      <TemplateCard
        sequenceKey="plan_expiry_5d"
        label="5-day reminder template"
        variableHint={
          <span>
            Variables: <code>{`{{1}}`}</code> = owner first name,{" "}
            <code>{`{{2}}`}</code> = plan label (Growth / Pro / Advanced),{" "}
            <code>{`{{3}}`}</code> = expiry date (e.g. "15 Jun 2026")
          </span>
        }
      />

      <TemplateCard
        sequenceKey="plan_expiry_1d"
        label="1-day reminder template"
        variableHint={
          <span>
            Variables: <code>{`{{1}}`}</code> = owner first name,{" "}
            <code>{`{{2}}`}</code> = plan label
          </span>
        }
      />

      <TemplateCard
        sequenceKey="plan_expiry_post"
        label="Post-expiry reminder template"
        allowHeaderImage
        variableHint={
          <span>
            Sent on the expiry day itself. Variables:{" "}
            <code>{`{{1}}`}</code> = owner first name,{" "}
            <code>{`{{2}}`}</code> = plan label. Upload the "expired today" image
            as the header before submitting to Meta.
          </span>
        }
      />

      <PostExpiryBlastCard />
    </PageWrapper>
  );
}
